import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const {
    FiPlus, FiEdit3, FiTrash2, FiSearch, FiFilter,
    FiUsers, FiX, FiBriefcase, FiInfo, FiChevronDown, FiCalendar, FiClock, FiShield, FiLayers
} = FiIcons;

const Customers = () => {
    const { user: currentUser } = useAuth();
    const [activeTab, setActiveTab] = useState('users');
    const [customers, setCustomers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        password: '',
        confirm_password: '',
        status: 'Active',
        subscription_tier: 'Free',
        team_name: '',
        member_limit: 10,
        role_name: 'User Role'
    });

    const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
    const [editingTeam, setEditingTeam] = useState(null);
    const [teamForm, setTeamForm] = useState({ team_name: '', lead_id: '', member_limit: 10 });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [customerData, roleData] = await Promise.all([
                api.get('users'),
                api.get('roles')
            ]);
            setCustomers(customerData.data.data || []);
            setRoles(roleData.data.data || []);
        } catch (error) {
            toast.error('Failed to load directory data');
        } finally {
            setLoading(false);
        }
    };

    const teams = useMemo(() => {
        const teamMap = {};
        customers.forEach(c => {
            const teamName = (c.team_name || '').trim();
            if (teamName) {
                if (!teamMap[teamName]) {
                    teamMap[teamName] = {
                        name: teamName,
                        lead: 'Not Assigned',
                        leadId: null,
                        count: 0,
                        member_limit: 10,
                        created_at: c.created_at
                    };
                }
                teamMap[teamName].count++;

                // Lead identification: Case-insensitive tier and role check
                const isTeamTier = (c.subscription_tier || '').toLowerCase() === 'team';
                const isLeadRole = (c.role_name || '').toLowerCase() === 'team lead' || (c.role_name || '').toLowerCase() === 'super admin';

                // If this user is a lead, prioritize their info for the team entry
                if (isTeamTier && isLeadRole) {
                    teamMap[teamName].lead = c.full_name || c.name;
                    teamMap[teamName].leadId = c.id;
                    teamMap[teamName].member_limit = c.member_limit || 10;
                    if (c.created_at) teamMap[teamName].created_at = c.created_at;
                }
            }
        });
        return Object.values(teamMap);
    }, [customers]);

    const potentialLeads = useMemo(() => {
        return customers.filter(c => {
            const isTeamTier = (c.subscription_tier || '').toLowerCase() === 'team';
            const isLeadRole = (c.role_name || '').toLowerCase() === 'team lead' || (c.role_name || '').toLowerCase() === 'super admin';
            return isTeamTier && isLeadRole;
        });
    }, [customers]);

    const usersWithCounts = useMemo(() => {
        return customers.map(user => {
            const team = teams.find(t => Number(t.leadId) === Number(user.id));
            return {
                ...user,
                member_count: team ? team.count : 0
            };
        });
    }, [customers, teams]);

    const handleSave = async () => {
        try {
            await api.saveCustomer({ ...(editingCustomer || {}), ...formData });
            await loadData();
            setIsModalOpen(false);
            toast.success('User updated successfully');
        } catch (e) { toast.error('Failed to save user'); }
    };

    const handleSaveTeam = async () => {
        try {
            if (!teamForm.team_name.trim()) {
                toast.error('Please enter a Team Name');
                return;
            }
            if (!teamForm.lead_id) {
                toast.error('Please select a Team Lead');
                return;
            }

            const leadId = Number(teamForm.lead_id);
            if (isNaN(leadId)) {
                toast.error('Invalid selection');
                return;
            }

            await api.updateTeamName(leadId, teamForm.team_name.trim(), Number(teamForm.member_limit));
            await loadData();
            setIsTeamModalOpen(false);
            toast.success('Team settings updated');
        } catch (e) { toast.error(e.message || 'Failed to update team'); }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this record?')) {
            try {
                await api.deleteCustomer(id);
                toast.success('Record deleted');
                loadData();
            } catch (e) { toast.error('Delete failed'); }
        }
    };

    const handleDeleteTeam = async (teamName) => {
        if (window.confirm(`Are you sure you want to delete the team "${teamName}"? All members will be unlinked from this team.`)) {
            try {
                await api.deleteTeam(teamName);
                toast.success('Team deleted successfully');
                loadData();
            } catch (e) {
                toast.error('Failed to delete team');
            }
        }
    };

    const filteredCustomers = usersWithCounts.filter(c =>
        (c.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) &&
        (filterStatus === 'all' || c.status === filterStatus)
    );

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active':
                return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
            case 'Inactive':
                return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400';
            default:
                return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400';
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Directory</h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">Manage personnel and organizational structures</p>
                    </div>
                    <button
                        onClick={() => {
                            if (activeTab === 'users') {
                                setEditingCustomer(null);
                                setFormData({ full_name: '', email: '', status: 'Active', subscription_tier: 'Free', team_name: '', member_limit: 10, role_name: 'User Role' });
                                setIsModalOpen(true);
                            } else {
                                setEditingTeam(null);
                                setTeamForm({ team_name: '', lead_id: '', member_limit: 10 });
                                setIsTeamModalOpen(true);
                            }
                        }}
                        className="flex items-center justify-center px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all shadow-md font-medium"
                    >
                        <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
                        {activeTab === 'users' ? 'Add User' : 'Add Team'}
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex space-x-1 mb-8 p-1 bg-gray-200/50 dark:bg-gray-800/50 rounded-xl w-fit border border-gray-200 dark:border-gray-700 shadow-sm">
                    <button onClick={() => setActiveTab('users')} className={`px-8 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'users' ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}>Users</button>
                    <button onClick={() => setActiveTab('teams')} className={`px-8 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'teams' ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}>Teams</button>
                </div>

                {/* Filters */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 mb-8 shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="relative">
                            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500/20" />
                        </div>
                        <div className="relative">
                            <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white outline-none appearance-none cursor-pointer">
                                <option value="all">All Status</option>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                            <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 dark:bg-gray-700/30">
                                <tr className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest border-b border-gray-200 dark:border-gray-700">
                                    {activeTab === 'users' ? (
                                        <>
                                            <th className="px-6 py-3">User</th>
                                            <th className="px-6 py-3">Email Address</th>
                                            <th className="px-6 py-3">Tier</th>
                                            <th className="px-6 py-3">Role</th>
                                            <th className="px-6 py-3">Last Login</th>
                                            <th className="px-6 py-3">Limit</th>
                                            <th className="px-6 py-3">Count</th>
                                            <th className="px-6 py-3">Status</th>
                                        </>
                                    ) : (
                                        <>
                                            <th className="px-6 py-3">Team Name</th>
                                            <th className="px-6 py-3">Team Lead</th>
                                            <th className="px-6 py-3">Count</th>
                                            <th className="px-6 py-3">Member Limit</th>
                                            <th className="px-6 py-3">Date Created</th>
                                        </>
                                    )}
                                    <th className="px-6 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {activeTab === 'users' ? (
                                    filteredCustomers.map(c => (
                                        <tr key={c.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors group">
                                            <td className="px-6 py-2.5">
                                                <div className="flex items-center">
                                                    <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-400 font-bold text-xs">{(c.full_name || '?').charAt(0)}</div>
                                                    <span className="ml-3 text-sm font-semibold text-gray-900 dark:text-white">{c.full_name}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-2.5 text-xs text-gray-600 dark:text-gray-300 font-medium">{c.email}</td>
                                            <td className="px-6 py-2.5">
                                                <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] font-bold text-gray-600 dark:text-gray-300">{c.subscription_tier}</span>
                                            </td>
                                            <td className="px-6 py-2.5">
                                                <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                                                    <SafeIcon icon={FiShield} className="w-3 h-3 mr-1.5 text-primary-500" />
                                                    {c.role_name || 'Standard User'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-2.5 text-xs text-gray-500 dark:text-gray-400">
                                                {formatDate(c.last_login_at)}
                                            </td>
                                            <td className="px-6 py-2.5 text-xs font-semibold text-gray-900 dark:text-white">
                                                {c.subscription_tier === 'Team' ? (c.member_limit || 10) : '-'}
                                            </td>
                                            <td className="px-6 py-2.5 text-xs font-semibold text-gray-900 dark:text-white">
                                                {c.subscription_tier === 'Team' ? c.member_count : '-'}
                                            </td>
                                            <td className="px-6 py-2.5">
                                                <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${getStatusColor(c.status)}`}>
                                                    {c.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-2.5 text-right">
                                                <div className="flex justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => { setEditingCustomer(c); setFormData({ ...c, member_limit: c.member_limit || 10, role_name: c.role_name || 'User Role' }); setIsModalOpen(true); }} className="p-1.5 text-gray-400 hover:text-primary-600 rounded-md transition-all"><SafeIcon icon={FiEdit3} className="w-3.5 h-3.5" /></button>
                                                    <button onClick={() => handleDelete(c.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-md transition-all"><SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    teams.map((t, i) => (
                                        <tr key={i} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors group">
                                            <td className="px-6 py-2.5">
                                                <div className="flex items-center">
                                                    <div className="p-1.5 bg-gray-100 dark:bg-gray-700 rounded mr-3"><SafeIcon icon={FiBriefcase} className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" /></div>
                                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{t.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-2.5 text-xs text-gray-600 dark:text-gray-300 font-medium">{t.lead}</td>
                                            <td className="px-6 py-2.5 text-xs font-bold text-gray-900 dark:text-white">{t.count}</td>
                                            <td className="px-6 py-2.5">
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-xs font-bold text-gray-900 dark:text-white">{t.member_limit}</span>
                                                    <div className="w-12 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                        <div className={`h-full rounded-full ${t.count >= t.member_limit ? 'bg-red-500' : 'bg-primary-600'}`} style={{ width: `${Math.min((t.count / t.member_limit) * 100, 100)}%` }}></div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-2.5 text-xs text-gray-500 dark:text-gray-400 font-medium">{formatDate(t.created_at)}</td>
                                            <td className="px-6 py-2.5 text-right">
                                                <div className="flex justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => { setEditingTeam(t); setTeamForm({ team_name: t.name, lead_id: String(t.leadId || ''), member_limit: t.member_limit }); setIsTeamModalOpen(true); }} className="p-1.5 text-gray-400 hover:text-primary-600 rounded-md transition-all"><SafeIcon icon={FiEdit3} className="w-3.5 h-3.5" /></button>
                                                    <button onClick={() => handleDeleteTeam(t.name)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-md transition-all"><SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* User Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-700/30">
                                <div className="flex items-center">
                                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg mr-3">
                                        <SafeIcon icon={FiUsers} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{editingCustomer ? 'Edit Personnel' : 'Add New Personnel'}</h3>
                                </div>
                                <button onClick={() => setIsModalOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg transition-colors"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
                            </div>
                            <div className="p-6 space-y-5">
                                <div className="grid grid-cols-2 gap-5">
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Full Name</label>
                                        <input type="text" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500/20" placeholder="John Doe" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Email Address</label>
                                        <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500/20" placeholder="john@example.com" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-5">
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Subscription Tier</label>
                                        <div className="relative">
                                            <select value={formData.subscription_tier} onChange={(e) => setFormData({ ...formData, subscription_tier: e.target.value })} className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white outline-none appearance-none">
                                                <option value="Free">Free</option>
                                                <option value="Pro">Pro</option>
                                                <option value="Team">Team</option>
                                            </select>
                                            <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">System Role</label>
                                        <div className="relative">
                                            <select value={formData.role_name} onChange={(e) => setFormData({ ...formData, role_name: e.target.value })} className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white outline-none appearance-none">
                                                {roles.map(role => (
                                                    <option key={role.id} value={role.name}>{role.name}</option>
                                                ))}
                                            </select>
                                            <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-5">
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Account Status</label>
                                        <div className="relative">
                                            <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white outline-none appearance-none">
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                            </select>
                                            <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
                                        </div>
                                    </div>
                                    {formData.subscription_tier === 'Team' && (
                                        <div className="space-y-1.5">
                                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Member Limit</label>
                                            <div className="relative">
                                                <input type="number" min="1" value={formData.member_limit} onChange={(e) => setFormData({ ...formData, member_limit: e.target.value })} className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500/20" />
                                                <SafeIcon icon={FiLayers} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {editingCustomer && (
                                    <div className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-100 dark:border-gray-600 flex items-center justify-between">
                                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                            <SafeIcon icon={FiClock} className="w-3.5 h-3.5 mr-2" />
                                            Last Login: {formatDate(editingCustomer.last_login_at)}
                                        </div>
                                        {editingCustomer.subscription_tier === 'Team' && (
                                            <div className="text-xs font-bold text-primary-600 dark:text-primary-400">
                                                Usage: {editingCustomer.member_count} / {editingCustomer.member_limit || 10}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="pt-4 flex gap-3">
                                    <button onClick={() => setIsModalOpen(false)} className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-bold text-xs uppercase tracking-widest transition-all">Cancel</button>
                                    <button onClick={handleSave} className="flex-2 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-bold text-xs uppercase tracking-widest transition-all shadow-lg shadow-primary-500/20">Save User Details</button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Team Modal */}
            <AnimatePresence>
                {isTeamModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-700/30">
                                <div className="flex items-center">
                                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg mr-3">
                                        <SafeIcon icon={FiBriefcase} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Team Setup</h3>
                                </div>
                                <button onClick={() => setIsTeamModalOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg transition-colors"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
                            </div>
                            <div className="p-6 space-y-5">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Team Name</label>
                                    <input type="text" value={teamForm.team_name} onChange={(e) => setTeamForm({ ...teamForm, team_name: e.target.value })} className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500/20" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Team Lead Assignment</label>
                                    <div className="relative">
                                        <select value={teamForm.lead_id} onChange={(e) => setTeamForm({ ...teamForm, lead_id: e.target.value })} className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white outline-none appearance-none">
                                            <option value="">Select a Lead</option>
                                            {potentialLeads.map(u => (
                                                <option key={u.id} value={u.id}>
                                                    {u.full_name} ({u.email}) {u.status !== 'Active' ? `[${u.status}]` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
                                    </div>
                                    {potentialLeads.length === 0 && (
                                        <p className="text-[10px] text-red-500 font-medium bg-red-50 dark:bg-red-900/10 p-2 rounded border border-red-100 dark:border-red-900/30">
                                            No users found with 'Team' tier and 'Team Lead' role. Please verify user settings in the Users tab.
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Team Capacity</label>
                                    <div className="relative">
                                        <input type="number" min="1" value={teamForm.member_limit} onChange={(e) => setTeamForm({ ...teamForm, member_limit: e.target.value })} className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500/20" />
                                        <SafeIcon icon={FiUsers} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    </div>
                                </div>
                                {editingTeam && (
                                    <div className="flex items-center px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-[11px] text-gray-500 dark:text-gray-400">
                                        <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5 mr-2" />
                                        System Record Created: {formatDate(editingTeam.created_at)}
                                    </div>
                                )}
                                <div className="p-3 bg-primary-50 dark:bg-primary-900/10 rounded-lg border border-primary-100 dark:border-primary-800 flex items-start space-x-3">
                                    <SafeIcon icon={FiInfo} className="w-3.5 h-3.5 text-primary-600 mt-0.5" />
                                    <p className="text-[10px] text-primary-700 dark:text-primary-400 leading-relaxed font-medium">As a Super Admin, you can manually override member limits for specific teams. To assign a lead, ensure the user has 'Team' tier and 'Team Lead' role.</p>
                                </div>
                                <button onClick={handleSaveTeam} className="w-full mt-2 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-bold text-xs uppercase tracking-widest transition-all shadow-lg shadow-primary-500/20">Apply Configuration</button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Customers;